import { get } from 'lodash';
import { generateErrorMessage } from './error';

/**
 * Builds an object that represents an async helper object containing initial state data
 * @param {*} data
 * @return {{loading: boolean, loaded: boolean, error: null, data: *}}
 */
export function asyncDefaults(data) {
  return {
    loading: false,
    loaded: false,
    error: null,
    data,
  };
}

// Default action step functions
// Function signature (dispatch, { getState, type, key }) => { return <action> }
export const startActionFn = (dispatch, { type }) => dispatch({ type });
// Function signature (dispatch, { getState, payload, type, key }) => { return <action> }
export const successActionFn = (dispatch, { payload, type }) => dispatch({ type, payload });
// Function signature (dispatch, { getState, error, message, type, key }) => { return <action> }
export const failureActionFn = (dispatch, { message, type }) => dispatch({ type, error: message });

/**
 * Wraps an asynchronous task with distinct reducer steps. Should be paired with makeAsyncReducer
 * @param {Object} [types] - action types
 * @param {string} [types.startType] - action type for start
 * @param {string} [types.successType] - action type for success
 * @param {string} [types.failureType] - action type for failure
 * @param {Function} thunkCallback - a function that returns function that returns a promise
 * @param {string} [key=undefined] - key or object path to value (uses lodash get)
 * @param {Object} [functions]
 * @param {Function} [functions.start=startActionFn]
 * @param {Function} [functions.success=successActionFn]
 * @param {Function} [functions.failure=failureActionFn]
 * @throws {Error} Throws an error when receiving bad data from the async function
 * @return {function(...[*]): Function} redux action function
 */
export function makeAsyncAction(
  { startType, successType, failureType },
  thunkCallback,
  doThrowError = true,
  key = undefined,
  resultFuncs = { start: startActionFn, success: successActionFn, failure: failureActionFn }
) {
  return (...args) => {
    const thunk = thunkCallback(...args);

    return async (dispatch, getState) => {
      resultFuncs.start(dispatch, { getState, type: startType, key });

      try {
        const response = await thunk(dispatch, getState);
        const payload = key !== undefined ? get(response, `${key}`) : response;

        if (payload === undefined) {
          throw new Error(`Did not receive data from the server.`);
        }

        resultFuncs.success(dispatch, { getState, payload, type: successType, key });

        return payload;
      } catch (error) {
        const message = generateErrorMessage(error);

        resultFuncs.failure(dispatch, { getState, error, message, type: failureType, key });

        // Can be caught with try {} catch (error) , or is using Promise syntax with .catch()
        if (doThrowError) {
          throw new Error(message);
        }
        return null;
      }
    };
  };
}

// Common async reducer functions, a success which will populate data coming from the server on success but
// allows replacement an example: success can be replaced in makeAsyncReducer to instead reset to the initial
// data on a delete instead.
// Function signatures (state, action, initial) => { return <state> }
const startReducerFn = (state) => ({ loading: true, loaded: false, error: null, data: state.data });
const successReducerFn = (state, action) => ({ loading: false, loaded: true, error: null, data: action.payload });
const failureReducerFn = (state, action) => ({
  loading: false,
  loaded: true,
  error: action.error,
  data: state.data,
});

/**
 * Creates a reducer to handle all makeAsyncAction action steps
 * @param {Object} [types] - action types
 * @param {string} [types.startType] - action type for start
 * @param {string} [types.successType] - action type for success
 * @param {string} [types.failureType] - action type for failure
 * @param {Object} [options]
 * @param {*} [options.initialData=null] - the initial data to use
 * @param {Function} [options.start=startReducerFn]
 * @param {Function} [options.success=successReducerFn]
 * @param {Function} [options.failure=failureReducerFn]
 * @return {function(state, action): *} redux reducer function
 */
export function makeAsyncReducer(
  { startType, successType, failureType },
  { initialData = null, start = startReducerFn, success = successReducerFn, failure = failureReducerFn } = {}
) {
  const initial = asyncDefaults(initialData);

  return (state = initial, action) => {
    switch (action.type) {
      case startType:
        return start(state, action, initialData);
      case successType:
        return success(state, action, initialData);
      case failureType:
        return failure(state, action, initialData);
      default:
        return state;
    }
  };
}

/**
 * Creates a simple reducer to reset the async helper state back to initial data
 * @param {string} type - action type
 * @param {*} [initialData=null]
 * @return {function(state, action): *} redux reducer function
 */
export function makeAsyncResetReducer(type, initialData = null) {
  return (state, action) => {
    if (action.type === type) {
      return asyncDefaults(initialData);
    }

    return state;
  };
}
